/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Link } from '@/components/link';
import { User } from 'lucide-react';
import { useAuth } from '@/providers/auth';
// @sfdc-extension-block-start SFDC_EXT_BRD_LOGIN_SIDEBAR
import { useAuthSheet } from '@/extensions/brd-login-sidebar/providers/auth-sheet';
import AuthDrawerMount from '@/extensions/brd-login-sidebar/components/auth-drawer-mount';
import { useLoyaltyContent } from '@/extensions/brd-login-sidebar/components/use-loyalty-content';
// @sfdc-extension-block-end SFDC_EXT_BRD_LOGIN_SIDEBAR
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    // @sfdc-extension-block-start SFDC_EXT_BRD_LOGIN_SIDEBAR
    const { openAuthSheet } = useAuthSheet();
    const { prefetch: prefetchLoyaltyContent } = useLoyaltyContent();
    // @sfdc-extension-block-end SFDC_EXT_BRD_LOGIN_SIDEBAR
    const isAuthenticated: boolean = useMemo(() => {
        // Check if user is authenticated (has valid token and is registered)
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);

    const icon = <User className="size-5" />;

    // Authenticated shoppers navigate to their account; guests open the inline
    // login/register sidebar instead of navigating to /login.
    const trigger = isAuthenticated ? (
        <Button
            variant="ghost"
            size="icon"
            className="cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity"
            asChild>
            <Link to="/account/overview" aria-label={tAccount('myAccount')}>
                {icon}
            </Link>
        </Button>
    ) : (
        <Button
            variant="ghost"
            size="icon"
            className="cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity"
            aria-label={t('signIn')}
            // @sfdc-extension-block-start SFDC_EXT_BRD_LOGIN_SIDEBAR
            onPointerEnter={prefetchLoyaltyContent}
            onFocus={prefetchLoyaltyContent}
            onClick={() => openAuthSheet('decision')}
            // @sfdc-extension-block-end SFDC_EXT_BRD_LOGIN_SIDEBAR
        >
            {icon}
        </Button>
    );

    return (
        <>
            <UserMenu isAuthenticated={isAuthenticated} trigger={trigger} />
            {/* @sfdc-extension-block-start SFDC_EXT_BRD_LOGIN_SIDEBAR */}
            {!isAuthenticated && <AuthDrawerMount />}
            {/* @sfdc-extension-block-end SFDC_EXT_BRD_LOGIN_SIDEBAR */}
        </>
    );
}
